/**
 * キャッシュパフォーマンス監視サービス
 * キャッシュのヒット率、サイズ、エラーなどのメトリクスを収集
 */
export class CacheMonitor {
  private static instance: CacheMonitor
  private metrics = {
    hits: 0,
    misses: 0,
    evictions: 0,
    errors: 0,
    totalSize: 0,
    averageLoadTime: 0,
    lastResetTime: Date.now(),
  }

  private loadTimes: number[] = []
  private readonly MAX_LOAD_TIME_SAMPLES = 100

  private constructor() {
    // シングルトンパターンのためプライベートコンストラクタ
  }

  static getInstance(): CacheMonitor {
    if (!CacheMonitor.instance) {
      CacheMonitor.instance = new CacheMonitor()
    }
    return CacheMonitor.instance
  }

  /**
   * キャッシュヒットを記録
   */
  recordHit(loadTimeMs?: number): void {
    this.metrics.hits++
    if (loadTimeMs !== undefined) {
      this.recordLoadTime(loadTimeMs)
    }
    this.logMetrics("cache_hit")
  }

  /**
   * キャッシュミスを記録
   */
  recordMiss(loadTimeMs?: number): void {
    this.metrics.misses++
    if (loadTimeMs !== undefined) {
      this.recordLoadTime(loadTimeMs)
    }
    this.logMetrics("cache_miss")
  }

  /**
   * キャッシュエビクション（削除）を記録
   */
  recordEviction(count = 1): void {
    this.metrics.evictions += count
    this.logMetrics("cache_eviction")
  }

  /**
   * エラーを記録
   */
  recordError(error: Error, context?: string): void {
    this.metrics.errors++
    console.error(`[CacheMonitor] Error in ${context || "unknown"}:`, error)
    this.logMetrics("cache_error")
  }

  /**
   * キャッシュサイズを更新
   */
  updateCacheSize(bytes: number): void {
    this.metrics.totalSize = bytes
  }

  /**
   * ロード時間を記録
   */
  private recordLoadTime(ms: number): void {
    this.loadTimes.push(ms)
    if (this.loadTimes.length > this.MAX_LOAD_TIME_SAMPLES) {
      this.loadTimes.shift()
    }
    this.metrics.averageLoadTime = this.loadTimes.reduce((sum, time) => sum + time, 0) / this.loadTimes.length
  }

  /**
   * キャッシュヒット率を取得
   */
  getHitRate(): number {
    const total = this.metrics.hits + this.metrics.misses
    return total > 0 ? (this.metrics.hits / total) * 100 : 0
  }

  /**
   * 現在のメトリクスを取得
   */
  getMetrics(): CacheMetrics {
    return {
      hitRate: this.getHitRate(),
      hits: this.metrics.hits,
      misses: this.metrics.misses,
      evictions: this.metrics.evictions,
      errors: this.metrics.errors,
      totalSizeMB: this.metrics.totalSize / (1024 * 1024),
      averageLoadTimeMs: this.metrics.averageLoadTime,
      uptimeHours: (Date.now() - this.metrics.lastResetTime) / (1000 * 60 * 60),
    }
  }

  /**
   * メトリクスをリセット
   */
  reset(): void {
    this.metrics = {
      hits: 0,
      misses: 0,
      evictions: 0,
      errors: 0,
      totalSize: this.metrics.totalSize, // サイズは保持
      averageLoadTime: 0,
      lastResetTime: Date.now(),
    }
    this.loadTimes = []
  }

  /**
   * メトリクスをコンソールに出力
   */
  printReport(): void {
    const metrics = this.getMetrics()
    console.log("=== Cache Performance Report ===")
    console.log(`Hit Rate: ${metrics.hitRate.toFixed(2)}%`)
    console.log(`Hits: ${metrics.hits}, Misses: ${metrics.misses}`)
    console.log(`Cache Size: ${metrics.totalSizeMB.toFixed(2)} MB`)
    console.log(`Avg Load Time: ${metrics.averageLoadTimeMs.toFixed(2)} ms`)
    console.log(`Errors: ${metrics.errors}`)
    console.log(`Uptime: ${metrics.uptimeHours.toFixed(2)} hours`)
    console.log("================================")
  }

  /**
   * Analytics/Crashlytics等へのメトリクス送信（実装は環境依存）
   */
  private logMetrics(event: string): void {
    // 開発環境でのみ詳細ログ
    if (__DEV__) {
      const metrics = this.getMetrics()
      console.log(`[CacheMonitor] ${event}`, {
        hitRate: `${metrics.hitRate.toFixed(2)}%`,
        totalSize: `${metrics.totalSizeMB.toFixed(2)}MB`,
      })
    }

    // 本番環境では集約してから送信（例：1分ごと）
    // Analytics.track(event, this.getMetrics())
  }

  /**
   * メモリ使用量を推定
   */
  estimateMemoryUsage(obj: any): number {
    try {
      const jsonString = JSON.stringify(obj)
      // UTF-16として計算（JavaScriptの文字列は内部的にUTF-16）
      return jsonString.length * 2
    } catch {
      return 0
    }
  }

  /**
   * キャッシュ効率を分析
   */
  analyzeEfficiency(): CacheEfficiencyAnalysis {
    const metrics = this.getMetrics()
    const efficiency = metrics.hitRate / 100

    return {
      status: efficiency > 0.8 ? "excellent" : efficiency > 0.6 ? "good" : efficiency > 0.4 ? "fair" : "poor",
      recommendation: this.getRecommendation(efficiency),
      costSavingsEstimate: this.estimateCostSavings(efficiency),
    }
  }

  private getRecommendation(efficiency: number): string {
    if (efficiency < 0.4) {
      return "キャッシュTTLを延長するか、プリフェッチ戦略の導入を検討してください"
    } else if (efficiency < 0.6) {
      return "キャッシュサイズを増やすことで、さらなる改善が期待できます"
    } else if (efficiency < 0.8) {
      return "良好なパフォーマンスです。微調整で最適化可能です"
    }
    return "優秀なキャッシュパフォーマンスです"
  }

  private estimateCostSavings(efficiency: number): number {
    // API呼び出し削減率からコスト削減を推定
    const baseMonthCost = 15000 // $15,000/月（キャッシュなしの場合）
    return baseMonthCost * efficiency
  }
}

export interface CacheMetrics {
  hitRate: number
  hits: number
  misses: number
  evictions: number
  errors: number
  totalSizeMB: number
  averageLoadTimeMs: number
  uptimeHours: number
}

export interface CacheEfficiencyAnalysis {
  status: "excellent" | "good" | "fair" | "poor"
  recommendation: string
  costSavingsEstimate: number
}
